import { combineReducers } from 'redux'
import * as types from 'models/actionTypes'
import sortByArray from 'utils/sortByArray'
import { furnitureItemStackables } from 'utils/stackables'
import { CATEGORIES as CATORDER, SUBCATEGORIES as SUBCATORDER } from 'constants/categories'
import { GEOMETRY_TYPE_MAP } from 'constants/renderItemTypes'
import { isEmpty } from 'utils/buildUrls'
import { getMockCommbinables } from 'services/mockdata'

const processProductItem = (product) => {
    const renderType = GEOMETRY_TYPE_MAP[product.geometryType]
    const thumbUrl = product.thumbUrl
    const isRug =
        (product.name && product.name.includes('rug')) ||
        (product.description && product.description.includes('rug')) ||
        (product.categories && product.categories.includes('Rugs'))
    const hasTallLegs = ['Tables & desks', 'Chairs'].some((category) => product.subCategories.includes(category))
    const isCubicStructure = [
        'Drawer units',
        'Dressers & storage drawers',
        'Storage solution systems',
        'Sideboards, buffets & sofa tables',
        'Display & storage cabinets',
        'Filing cabinets & storage cabinets',
        'TV & media furniture',
        'Shelving units, bookcases & storage options',
        'Clothes organizers',
        'Nightstands',
        'Bathroom storage',
    ].some((category) => product.subCategories.includes(category))

    return {
        [product.id]: {
            id: product.id,
            region: product.region,
            name: product.name,
            thumbUrl,
            collections: product.collections,
            rootCategories: product.rootCategories,
            categories: product.categories,
            subCategories: product.subCategories,
            ecommerceUrl: product.ecommerceUrl,
            globalProductId: product.globalProductId,
            price: product.price || '$0.00',
            rating: product.rating,
            ratingCount: product.ratingCount,
            description: product.description,
            imageUrls: product.imageUrls,
            qaStatus: product.qaStatus,
            availableInRegion: product.availableInRegion,
            uncompressedModelSize: product.uncompressedModelSize,
            localItemNumber: product.localItemNumber,
            recommended: product.recommended,
            subCatalogs: product.subCatalogs,
            swappableItems: null,
            renderType,

            // Furniture props
            modelUrl: product.modelUrl,
            rotX: product.rotX, // TODO check proptypes
            scale: product.scale,
            max: { x: product.maxX, y: product.maxY, z: product.maxZ },
            min: { x: product.minX, y: product.minY, z: product.minZ },
            stacks: product.stacks, // TODO check proptypes
            stackables: furnitureItemStackables(product), // TODO check proptypes
            isRug,
            metadata: product.metadata,
            hidden: product.hidden || false,
            hasTallLegs,
            isCubicStructure,

            // Art props
            artAssetUrls: product.processedImageUrls,
            displayHeightCm: product.displayHeightCm,
            displayWidthCm: product.displayWidthCm,

            // Admin properties
            globalProductAdminUrl: product.globalProductAdminUrl,
            localProductAdminUrl: product.localProductAdminUrl,
        },
    }
}

export const initialState = {
    byId: {
        ...getMockCommbinables(processProductItem),
    },
    selectedId: null,
    swapAnchorId: null,
    selectedSwapDetailsId: null,
    filter: {
        query: null,
        category: '*',
        subCategory: '*',
        subCatalog: '*',
        roomType: '*',
    },
    loadedSubCatalogs: [],
    isLoading: false,
    isSwappablesLoading: false,
    isCategoriesLoading: false,
    isLoadingByIds: false,
    stackEnabled: true, // TODO remove entirely
    categories: [],
    filterResult: {
        ids: [],
        count: 0,
        currentPage: 0,
        hasNext: true,
        moreToken: null,
    },
}

export const byId = (state = initialState.byId, action) => {
    switch (action.type) {
        case types.RECEIVE_FURNITURE:
        case types.RECEIVE_FURNITURE_BY_IDS:
        case types.RECEIVE_FURNITURE_BY_LOCALITEMNOS:
        case types.RECEIVE_COMPILED_COMPOSITION:
            return Object.assign({}, state, ...action.furniture.map(processProductItem))
        case types.RECEIVE_FURNITURE_SWAPPABLES:
            return Object.assign({}, state, ...action.furniture.map(processProductItem), {
                [action.id]: {
                    ...state[action.id],
                    swappableItems: action.furniture.map((furni) => furni.id),
                },
            })
        case types.RECEIVE_FURNITURE_COMBINABLES: {
            const newState = { ...state }
            newState[action.parentId].metadata.combinables.nodesMeta[action.nodeIndex].items = action.furniture.map(
                (furni) => furni.id,
            )
            return newState
        }
        default:
            return state
    }
}

export const selectedId = (state = initialState.selectedId, action) => {
    switch (action.type) {
        case types.SELECT_FURNITURE:
            return action.id
        default:
            return state
    }
}

// swapAnchorId is different from the selectedId and is used to display
// a consistent set of swappable items for the original selected furniture.
// The selectedId value will change as furniture is swapped, while
// the swapAnchorId state maintains the original selection.
export const swapAnchorId = (state = initialState.swapAnchorId, action) => {
    switch (action.type) {
        case types.PLACE_RENDER_ITEM: {
            const {
                settings: { combinablePlacement },
                id,
            } = action

            if (!combinablePlacement || isEmpty(combinablePlacement) || !combinablePlacement.parentId) {
                return id
            }
            return state
        }
        case types.SHOW_SWAP_MENU:
            return action.id
        default:
            return state
    }
}

export const selectedSwapDetailsId = (state = initialState.selectedSwapDetailsId, action) => {
    switch (action.type) {
        case types.SELECT_FURNITURE_SWAP_DETAILS:
            return action.id
        default:
            return state
    }
}

export const filter = (state = initialState.filter, action) => {
    switch (action.type) {
        case types.FURNITURE_FILTER_UPDATE: {
            const nextFilter = {
                ...state,
                ...action.filter,
            }
            if (Object.prototype.hasOwnProperty.call(action.filter, 'query')) {
                nextFilter.category = '*'
                nextFilter.subCategory = '*'
                nextFilter.subCatalog = '*'
                nextFilter.roomType = '*'
            } else if (action.filter.subCatalog) {
                nextFilter.query = null
                nextFilter.category = '*'
                nextFilter.subCategory = '*'
                nextFilter.roomType = '*'
            } else if (action.filter.roomType) {
                nextFilter.query = null
                nextFilter.category = '*'
                nextFilter.subCategory = '*'
                nextFilter.subCatalog = '*'
            } else if (action.filter.category) {
                nextFilter.query = null
                nextFilter.subCatalog = '*'
                nextFilter.roomType = '*'
                if (!action.filter.subCategory) {
                    nextFilter.subCategory = '*'
                }
            }
            return nextFilter
        }
        case types.FURNITURE_FILTER_CLEAR:
            return {
                ...initialState.filter,
            }
        default:
            return state
    }
}

const isLoading = (state = initialState.isLoading, action) => {
    switch (action.type) {
        case types.REQUEST_FURNITURE_SAGA:
            return true
        case types.RECEIVE_FURNITURE:
        case types.FAILED_RECEIVE_FURNITURE:
            return false
        default:
            return state
    }
}

export const isSwappablesLoading = (state = initialState.isSwappablesLoading, action) => {
    switch (action.type) {
        case types.REQUEST_FURNITURE_SWAPPABLES_SAGA:
            return true
        case types.RECEIVE_FURNITURE_SWAPPABLES:
        case types.FAILED_RECEIVE_FURNITURE_SWAPPABLES:
            return false
        default:
            return state
    }
}

const isCategoriesLoading = (state = initialState.isCategoriesLoading, action) => {
    switch (action.type) {
        case types.REQUEST_FURNITURE_CATEGORIES_SAGA:
            return true
        case types.RECEIVE_FURNITURE_CATEGORIES:
        case types.FAILED_RECEIVE_FURNITURE_CATEGORIES:
            return false
        default:
            return state
    }
}

export const isLoadingByIds = (state = initialState.isLoadingByIds, action) => {
    switch (action.type) {
        case types.REQUEST_FURNITURE_BY_IDS_SAGA:
            return true
        case types.RECEIVE_FURNITURE_BY_IDS:
        case types.FAILED_RECEIVE_FURNITURE_BY_IDS:
            return false
        default:
            return state
    }
}

export const stackEnabled = (state = initialState.stackEnabled, action) => {
    switch (action.type) {
        case types.SET_MAGIC_WINDOW_LOADING:
            return true
        default:
            return state
    }
}

export const categories = (state = initialState.categories, action) => {
    switch (action.type) {
        case types.RECEIVE_FURNITURE_CATEGORIES:
            if (action.categories && action.categories.length) {
                return sortByArray(
                    action.categories.map((cat) => ({
                        key: cat.categoryId,
                        label: cat.name,
                        subCategories: sortByArray(
                            cat.subCategories.map((subCat) => ({
                                key: subCat.categoryId,
                                label: subCat.name,
                            })),
                            [(a) => a.label, SUBCATORDER],
                            (a) => a.label,
                        ),
                    })),
                    [(a) => a.label, CATORDER],
                    (a) => a.label,
                )
            }
            return state
        default:
            return state
    }
}

export const filterResult = (state = initialState.filterResult, action) => {
    switch (action.type) {
        case types.RECEIVE_FURNITURE:
            return {
                ids: [...state.ids, ...action.furniture.map((furni) => furni.id)],
                count: action.count || 0,
                currentPage: state.currentPage + 1,
                hasNext: action.hasNext || false,
                moreToken: action.moreToken || null,
            }
        case types.FURNITURE_FILTER_CLEAR:
        case types.FURNITURE_FILTER_UPDATE:
            return initialState.filterResult
        default:
            return state
    }
}

export default combineReducers({
    byId,
    selectedId,
    filter,
    isLoading,
    isSwappablesLoading,
    isCategoriesLoading,
    isLoadingByIds,
    stackEnabled,
    categories,
    filterResult,
    swapAnchorId,
    selectedSwapDetailsId,
})
